import {
    Box,
    Button,
    HStack,
    VStack,
    Text,
    useDisclosure,
    Flex,
    Spinner,
} from "@chakra-ui/react";
import { LoanSignature } from "@common/loans/signatures/loan-signature.model";
import { supabase } from "index";
import { useCustomFormats } from "hooks/useCustomFormat";
import { SignatureModal } from "./signature-modal";
import { useEffect, useState, useContext } from "react";
import { LoanContext } from "providers/loan";

interface SignatureSectionProps {
    type: "borrower" | "co-borrower";
    signature?: LoanSignature;
    onSignatureChange: (signature: LoanSignature) => void;
}

export function SignatureSection({
    type,
    signature,
    onSignatureChange,
}: SignatureSectionProps) {
    const { loan } = useContext(LoanContext);
    const { formatDateTime } = useCustomFormats();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [signatureDataUrl, setSignatureDataUrl] = useState<string>();

    useEffect(() => {
        if (signature) {
            getSignatureImg(signature).then((img) => {
                setSignatureDataUrl(img);
            });
        }
    }, [signature]);

    async function getSignatureImg(signature: LoanSignature): Promise<string> {
        const { data } = supabase.storage
            .from("signatures")
            .getPublicUrl(signature.signature_url);

        const img = await fetch(data.publicUrl);
        return URL.createObjectURL(await img.blob());
    }

    function userInfoStack(label: string, value: string) {
        return (
            <HStack justifyContent="space-between">
                <Text fontWeight="bold">{label}</Text>
                <Text>{value}</Text>
            </HStack>
        );
    }

    function handleSignatureChange(signature: LoanSignature) {
        setSignatureDataUrl(undefined);
        onSignatureChange(signature);
        onClose();
    }

    return (
        <Flex direction="column" alignItems="center" flex={1}>
            <Text mb={2} textTransform="capitalize">
                {type} Signature
            </Text>
            <VStack gap={2} w="100%">
                <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={2}
                    width="100%"
                    height="100px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    {!signature ? (
                        <Text color="gray.400">No signature</Text>
                    ) : signatureDataUrl ? (
                        <img
                            src={signatureDataUrl}
                            alt="Borrower Signature"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                            }}
                        />
                    ) : (
                        <Spinner />
                    )}
                </Box>
                {signature && (
                    <Box
                        width="100%"
                        p={2}
                        bg="gray.50"
                        borderRadius="md"
                        fontSize="xs"
                    >
                        {userInfoStack("Signed by:", signature.user_full_name)}
                        {userInfoStack(
                            "IP Address:",
                            signature.user_ip_address
                        )}
                        {userInfoStack(
                            "Date/Time:",
                            formatDateTime(signature.created_at)
                        )}
                    </Box>
                )}
            </VStack>
            {!loan!.status.isSubmitted && (
                <Button mt={2} colorScheme="blue" onClick={onOpen}>
                    {signature ? "Edit Signature" : "Sign"}
                </Button>
            )}

            {isOpen && (
                <SignatureModal
                    type={type}
                    onClose={onClose}
                    signature={signature}
                    onSignatureChange={handleSignatureChange}
                    signatureDataUrl={signatureDataUrl}
                />
            )}
        </Flex>
    );
}
